import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    IconButton,
    Alert,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    InputAdornment,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonOff as DisableIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    ContentCopy as CopyIcon,
    AutoFixHigh as GenerateIcon
} from '@mui/icons-material';
import axios from 'axios';

const ACTIVITY_FILTERS = [
    { value: 'all', label: 'All Users' },
    { value: 'active_recently', label: 'Active Recently' },
    { value: 'no_activity_4', label: 'No Activity Last 4 Days' },
    { value: 'no_activity_14', label: 'No Activity Last 14 Days' },
    { value: 'never_active', label: 'Never Active' },
    { value: 'disabled', label: 'Disabled / Inactive' }
];

const ACTIVITY_STATUS_COLORS = {
    active_recently: 'success',
    no_activity_4: 'warning',
    no_activity_14: 'error',
    never_active: 'default',
    needs_review: 'info'
};

const USER_TABLE_CELL_SX = {
    px: 1.5,
    py: 1.15,
    fontSize: '0.86rem',
    verticalAlign: 'top',
    whiteSpace: 'normal',
    overflowWrap: 'normal',
    wordBreak: 'normal'
};

const COMPACT_TEXT_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    wordBreak: 'normal',
    overflowWrap: 'normal'
};

const generateSecurePassword = () => {
    const groups = [
        'ABCDEFGHJKLMNPQRSTUVWXYZ',
        'abcdefghijkmnopqrstuvwxyz',
        '23456789',
        '!@#$%&*'
    ];
    const allChars = groups.join('');
    const randomValues = new Uint32Array(16);
    window.crypto.getRandomValues(randomValues);

    const pick = (chars, index) => chars[randomValues[index] % chars.length];
    const passwordChars = [
        pick(groups[0], 0),
        pick(groups[1], 1),
        pick(groups[2], 2),
        pick(groups[3], 3),
        ...Array.from({ length: 8 }, (_, index) => pick(allChars, index + 4))
    ];

    return passwordChars
        .map((char, index) => ({ char, sort: randomValues[index + 4] }))
        .sort((a, b) => a.sort - b.sort)
        .map((item) => item.char)
        .join('');
};

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [agentGroups, setAgentGroups] = useState([]);
    const [activitySummary, setActivitySummary] = useState(null);
    const [activityError, setActivityError] = useState('');
    const [selectedAgentGroupId, setSelectedAgentGroupId] = useState('all');
    const [activityFilter, setActivityFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [groupDialogMode, setGroupDialogMode] = useState('create');
    const [selectedAgentGroup, setSelectedAgentGroup] = useState(null);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordCopied, setPasswordCopied] = useState(false);
    const [groupFormData, setGroupFormData] = useState({ name: '' });
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        agentGroupId: ''
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setActivityError('');
            const [usersResponse, groupsResponse, activityResponse] = await Promise.all([
                axios.get('/api/users'),
                axios.get('/api/users/agent-groups'),
                axios.get('/api/users/activity-summary').catch((activityError) => {
                    console.error('Failed to fetch activity summary:', activityError);
                    setActivityError('Activity summary is unavailable. User list loaded, but activity filters need review.');
                    return { data: null };
                })
            ]);

            setUsers(usersResponse.data.users);
            setAgentGroups(groupsResponse.data.groups || []);
            setActivitySummary(activityResponse.data);
        } catch (error) {
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (mode, user = null) => {
        setDialogMode(mode);
        setSelectedUser(user);
        setIsAdmin(mode === 'createAdmin' || (user && user.isAdmin));
        
        if (user) {
            setFormData({
                name: user.name,
                username: user.username || '',
                email: user.email,
                phone: user.phone || '',
                password: '',
                agentGroupId: user.agentGroupId || ''
            });
        } else {
            const preselectedGroupId = mode === 'create' && selectedAgentGroupId !== 'all' && selectedAgentGroupId !== 'unassigned'
                ? selectedAgentGroupId
                : '';
            setFormData({
                name: '',
                username: '',
                email: '',
                phone: '',
                password: '',
                agentGroupId: preselectedGroupId
            });
        }
        setDialogOpen(true);
        setError('');
        setShowPassword(false);
        setPasswordCopied(false);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedUser(null);
        setFormData({ name: '', username: '', email: '', phone: '', password: '', agentGroupId: '' });
        setError('');
        setShowPassword(false);
        setPasswordCopied(false);
    };

    const handleOpenGroupDialog = (mode, group = null) => {
        setGroupDialogMode(mode);
        setSelectedAgentGroup(group);
        setGroupFormData({ name: group?.name || '' });
        setGroupDialogOpen(true);
        setError('');
    };

    const handleCloseGroupDialog = () => {
        setGroupDialogOpen(false);
        setSelectedAgentGroup(null);
        setGroupFormData({ name: '' });
        setError('');
    };

    const handleSaveAgentGroup = async () => {
        try {
            setError('');

            if (groupDialogMode === 'rename' && selectedAgentGroup) {
                await axios.put(`/api/users/agent-groups/${selectedAgentGroup.id}`, groupFormData);
                setSuccess('Agent group renamed successfully');
            } else {
                const response = await axios.post('/api/users/agent-groups', groupFormData);
                const newGroupId = response.data.group?.id;
                if (newGroupId) {
                    setSelectedAgentGroupId(String(newGroupId));
                }
                setSuccess('Agent group created successfully');
            }

            handleCloseGroupDialog();
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to save agent group');
        }
    };

    const handleSubmit = async () => {
        try {
            setError('');
            
            if (dialogMode === 'create' || dialogMode === 'createAdmin') {
                // Create new user
                const endpoint = isAdmin ? '/api/users/admins' : '/api/users/agents';
                await axios.post(endpoint, isAdmin ? {
                    name: formData.name,
                    username: formData.username,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password
                } : formData);
                setSuccess(`${isAdmin ? 'Admin' : 'Agent'} created successfully`);
            } else {
                // Edit existing user
                const endpoint = selectedUser.isAdmin ? 
                    `/api/users/admins/${selectedUser.id}` : 
                    `/api/users/agents/${selectedUser.id}`;
                const payload = {
                    name: formData.name,
                    username: formData.username,
                    email: formData.email,
                    phone: formData.phone,
                    agentGroupId: selectedUser.isAdmin ? undefined : formData.agentGroupId
                };

                if (!selectedUser.isAdmin && formData.password.trim()) {
                    payload.password = formData.password;
                }

                await axios.put(endpoint, payload);
                setSuccess('User updated successfully');
            }
            
            handleCloseDialog();
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Operation failed');
        }
    };

    const handleDisable = async (user) => {
        try {
            await axios.put(`/api/users/agents/${user.id}/disable`);
            setSuccess('Agent disabled successfully');
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to disable agent');
        }
    };

    const handleDelete = async (user) => {
        if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) {
            return;
        }

        try {
            const endpoint = user.isAdmin ? 
                `/api/users/admins/${user.id}` : 
                `/api/users/agents/${user.id}`;
            await axios.delete(endpoint);
            setSuccess('User deleted successfully');
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete user');
        }
    };

    const handleGeneratePassword = () => {
        setFormData((current) => ({ ...current, password: generateSecurePassword() }));
        setShowPassword(true);
        setPasswordCopied(false);
    };

    const handleCopyPassword = async () => {
        if (!formData.password || !navigator.clipboard) {
            return;
        }

        try {
            await navigator.clipboard.writeText(formData.password);
            setPasswordCopied(true);
            window.setTimeout(() => setPasswordCopied(false), 1800);
        } catch (copyError) {
            setError('Could not copy password. You can reveal it and copy it manually.');
        }
    };

    const activityByUserId = useMemo(() => {
        const map = new Map();
        (activitySummary?.users || []).forEach((activity) => {
            map.set(Number(activity.userId), activity);
        });
        return map;
    }, [activitySummary]);

    const usersWithActivity = useMemo(() => {
        return users
            .map((user) => {
                const activity = activityByUserId.get(Number(user.id)) || {
                    userId: user.id,
                    lastTrackedActivityAt: null,
                    lastSentOfferAt: null,
                    sentOffersLast4Days: 0,
                    sentOffersLast14Days: 0,
                    sentOffersTotal: 0,
                    activityStatus: 'needs_review',
                    activityStatusLabel: 'Needs Review',
                    isActiveLast4Days: false,
                    isActiveLast14Days: false,
                    hasTrackedActivity: false
                };

                return { ...user, activity };
            })
            .sort((a, b) => {
                const aCreated = new Date(a.createdAt).getTime() || 0;
                const bCreated = new Date(b.createdAt).getTime() || 0;
                if (aCreated !== bCreated) {
                    return bCreated - aCreated;
                }
                return Number(b.id || 0) - Number(a.id || 0);
            });
    }, [users, activityByUserId]);

    const agentGroupEligibleUsers = useMemo(
        () => usersWithActivity.filter((user) => user.accessProfile !== 'processing'),
        [usersWithActivity]
    );

    const groupOptions = useMemo(() => [
        {
            id: 'all',
            name: 'All Users',
            count: usersWithActivity.length,
            helperText: 'All admin and agent accounts'
        },
        {
            id: 'unassigned',
            name: 'Unassigned',
            count: agentGroupEligibleUsers.filter((user) => !user.agentGroupId).length,
            helperText: 'Users not assigned to a group'
        },
        ...agentGroups.map((group) => ({
            id: String(group.id),
            name: group.name,
            count: agentGroupEligibleUsers.filter((user) => Number(user.agentGroupId) === Number(group.id)).length,
            group
        }))
    ], [agentGroups, usersWithActivity, agentGroupEligibleUsers]);

    const groupFilteredUsers = useMemo(() => {
        if (selectedAgentGroupId === 'unassigned') {
            return agentGroupEligibleUsers.filter((user) => !user.agentGroupId);
        }

        if (selectedAgentGroupId !== 'all') {
            return agentGroupEligibleUsers.filter((user) => String(user.agentGroupId || '') === selectedAgentGroupId);
        }

        return usersWithActivity;
    }, [selectedAgentGroupId, usersWithActivity, agentGroupEligibleUsers]);

    const filterCounts = useMemo(() => {
        const activityEligibleUsers = groupFilteredUsers.filter((user) => user.accessProfile !== 'processing');
        return {
            all: groupFilteredUsers.length,
            active_recently: activityEligibleUsers.filter((user) => user.activity.isActiveLast4Days).length,
            no_activity_4: activityEligibleUsers.filter((user) => !user.activity.isActiveLast4Days).length,
            no_activity_14: activityEligibleUsers.filter((user) => !user.activity.isActiveLast14Days).length,
            never_active: activityEligibleUsers.filter((user) => user.activity.activityStatus === 'never_active').length,
            disabled: activityEligibleUsers.filter((user) => !user.isActive).length
        };
    }, [groupFilteredUsers]);

    const filteredUsers = useMemo(() => {
        const activityEligibleUsers = groupFilteredUsers.filter((user) => user.accessProfile !== 'processing');
        switch (activityFilter) {
            case 'active_recently':
                return activityEligibleUsers.filter((user) => user.activity.isActiveLast4Days);
            case 'no_activity_4':
                return activityEligibleUsers.filter((user) => !user.activity.isActiveLast4Days);
            case 'no_activity_14':
                return activityEligibleUsers.filter((user) => !user.activity.isActiveLast14Days);
            case 'never_active':
                return activityEligibleUsers.filter((user) => user.activity.activityStatus === 'never_active');
            case 'disabled':
                return activityEligibleUsers.filter((user) => !user.isActive);
            default:
                return groupFilteredUsers;
        }
    }, [activityFilter, groupFilteredUsers]);

    const formatDateTime = (value) => {
        if (!value) return '-';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleString();
    };

    const getActivityChip = (activity) => (
        <Chip
            label={activity.activityStatusLabel || 'Needs Review'}
            color={ACTIVITY_STATUS_COLORS[activity.activityStatus] || 'info'}
            size="small"
            variant={activity.activityStatus === 'never_active' ? 'outlined' : 'filled'}
            sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            }}
        />
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">User Management</Typography>
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                        sx={{ mr: 1 }}
                    >
                        Add Agent
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('createAdmin')}
                    >
                        Add Admin
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {activityError && <Alert severity="warning" sx={{ mb: 2 }}>{activityError}</Alert>}
            <Alert severity="info" sx={{ mb: 2 }}>
                Review only. Deactivate/delete users only after owner confirmation. Activity filters are based on tracked portal activity, not guaranteed login tracking.
                {activitySummary?.loginTracking?.durableLastLoginTracked ? '' : ' Durable last-login tracking is not available.'}
            </Alert>
            {activitySummary?.warnings?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Activity summary loaded with warnings: {activitySummary.warnings.slice(0, 2).join(' ')}
                </Alert>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5}>
                    <Box>
                        <Typography variant="h6">Agent Groups</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Organization only. Agent groups do not change login, permissions, access, or sales tools.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenGroupDialog('create')}
                    >
                        Add New Group
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                        gap: 1.5
                    }}
                >
                    {groupOptions.map((option) => {
                        const selected = selectedAgentGroupId === option.id;
                        return (
                            <Paper
                                key={option.id}
                                variant="outlined"
                                onClick={() => {
                                    setSelectedAgentGroupId(option.id);
                                    setActivityFilter('all');
                                }}
                                sx={{
                                    p: 1.5,
                                    cursor: 'pointer',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    bgcolor: selected ? 'rgba(75, 40, 109, 0.08)' : 'background.paper',
                                    boxShadow: selected ? '0 0 0 1px rgba(75, 40, 109, 0.20)' : 'none'
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                    <Box minWidth={0}>
                                        <Typography variant="subtitle2" noWrap>{option.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.count} user{option.count === 1 ? '' : 's'}
                                        </Typography>
                                    </Box>
                                    {option.group && (
                                        <Button
                                            size="small"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenGroupDialog('rename', option.group);
                                            }}
                                        >
                                            Rename
                                        </Button>
                                    )}
                                </Box>
                                {option.helperText && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                        {option.helperText}
                                    </Typography>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            </Paper>

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={activityFilter}
                    onChange={(event, nextFilter) => setActivityFilter(nextFilter)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {ACTIVITY_FILTERS.map((filter) => (
                        <Tab
                            key={filter.value}
                            value={filter.value}
                            label={`${filter.label} (${filterCounts[filter.value] || 0})`}
                        />
                    ))}
                </Tabs>
            </Paper>

            <TableContainer component={Paper} sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ width: '100%', minWidth: 1240, tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: '14%' }}>Name</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: '20%' }}>Username / Email</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 78 }}>Role</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 100 }}>Status</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: '16%' }}>Agent Group</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 148 }}>Activity</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 118 }}>Last Activity</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 118 }}>Last Sent</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 58 }} align="right">4d</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 62 }} align="right">14d</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 64 }} align="right">Total</TableCell>
                            <TableCell sx={{ ...USER_TABLE_CELL_SX, width: 104 }}>Created</TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...USER_TABLE_CELL_SX,
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 3,
                                    width: 116,
                                    minWidth: 116,
                                    bgcolor: 'background.paper',
                                    boxShadow: '-8px 0 12px rgba(0,0,0,0.04)'
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    <Typography variant="body2" sx={COMPACT_TEXT_SX}>{user.name}</Typography>
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    <Typography variant="body2" sx={COMPACT_TEXT_SX}>{user.username || '-'}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={COMPACT_TEXT_SX}>{user.email}</Typography>
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    <Chip
                                        label={user.accessProfile === 'processing' ? 'Processing' : (user.isAdmin ? 'Admin' : 'Agent')}
                                        color={user.accessProfile === 'processing' ? 'warning' : (user.isAdmin ? 'secondary' : 'primary')}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    <Chip
                                        label={user.isActive ? 'Active' : 'Disabled'}
                                        color={user.isActive ? 'success' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    <Typography variant="body2" sx={COMPACT_TEXT_SX}>
                                        {user.accessProfile === 'processing' ? 'Not applicable' : (user.agentGroupName || 'Unassigned')}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    {user.accessProfile === 'processing' ? <Chip label="Not tracked" size="small" variant="outlined" /> : getActivityChip(user.activity)}
                                </TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>{user.accessProfile === 'processing' ? '-' : formatDateTime(user.activity.lastTrackedActivityAt)}</TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>{user.accessProfile === 'processing' ? '-' : formatDateTime(user.activity.lastSentOfferAt)}</TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX} align="right">{user.accessProfile === 'processing' ? '-' : (user.activity.sentOffersLast4Days || 0)}</TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX} align="right">{user.accessProfile === 'processing' ? '-' : (user.activity.sentOffersLast14Days || 0)}</TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX} align="right">{user.accessProfile === 'processing' ? '-' : (user.activity.sentOffersTotal || 0)}</TableCell>
                                <TableCell sx={USER_TABLE_CELL_SX}>
                                    {new Date(user.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        ...USER_TABLE_CELL_SX,
                                        position: 'sticky',
                                        right: 0,
                                        zIndex: 2,
                                        width: 116,
                                        minWidth: 116,
                                        bgcolor: 'background.paper',
                                        boxShadow: '-8px 0 12px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    <Box sx={{ display: 'inline-flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog('edit', user)}
                                            title="Edit"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        {!user.isAdmin && user.isActive && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDisable(user)}
                                                title="Disable"
                                            >
                                                <DisableIcon />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(user)}
                                            title="Delete"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit User Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' || dialogMode === 'createAdmin' 
                        ? `Create New ${isAdmin ? 'Admin' : 'Agent'}` 
                        : `Edit ${selectedUser?.accessProfile === 'processing' ? 'Processing Account' : (selectedUser?.isAdmin ? 'Admin' : 'Agent')}`}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Username (optional)"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        margin="normal"
                        helperText="Leave blank to login with email only"
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        margin="normal"
                        helperText={!isAdmin ? "Agent's phone will be auto-filled in email offers" : ""}
                    />
                    {!isAdmin && selectedUser?.accessProfile !== 'processing' && (
                        <TextField
                            select
                            fullWidth
                            label="Agent Group"
                            value={formData.agentGroupId}
                            onChange={(e) => setFormData({ ...formData, agentGroupId: e.target.value })}
                            margin="normal"
                            helperText="Organization only. This does not change access or permissions."
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {agentGroups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {(dialogMode === 'create' || dialogMode === 'createAdmin' || (dialogMode === 'edit' && selectedUser && !selectedUser.isAdmin)) && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
                                <Button
                                    size="small"
                                    startIcon={<GenerateIcon />}
                                    onClick={handleGeneratePassword}
                                >
                                    Generate
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<CopyIcon />}
                                    onClick={handleCopyPassword}
                                    disabled={!formData.password}
                                >
                                    {passwordCopied ? 'Copied' : 'Copy'}
                                </Button>
                            </Box>
                            <TextField
                                fullWidth
                                label={dialogMode === 'edit' ? 'New Password / Reset Password' : 'Password'}
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => {
                                    setFormData({ ...formData, password: e.target.value });
                                    setPasswordCopied(false);
                                }}
                                required={dialogMode === 'create' || dialogMode === 'createAdmin'}
                                helperText={
                                    dialogMode === 'edit'
                                        ? 'Leave blank to keep current password. New passwords are saved hashed; plaintext is not stored.'
                                        : 'Minimum 8 characters with uppercase, lowercase, and number. Passwords are saved hashed; plaintext is not stored.'
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                onClick={() => setShowPassword((current) => !current)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    )}
                    {dialogMode === 'edit' && selectedUser && !selectedUser.isAdmin && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Existing passwords are never shown here. Leaving password blank keeps the current password unchanged.
                        </Alert>
                    )}
                    {(dialogMode === 'create' || dialogMode === 'createAdmin') && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isAdmin}
                                    onChange={(e) => setIsAdmin(e.target.checked)}
                                />
                            }
                            label="Admin User"
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {dialogMode === 'create' || dialogMode === 'createAdmin' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={groupDialogOpen} onClose={handleCloseGroupDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {groupDialogMode === 'rename' ? 'Rename Agent Group' : 'Add New Agent Group'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Group Name"
                        value={groupFormData.name}
                        onChange={(e) => setGroupFormData({ name: e.target.value })}
                        margin="normal"
                        required
                        helperText="Agent groups are for organization only."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGroupDialog}>Cancel</Button>
                    <Button onClick={handleSaveAgentGroup} variant="contained">
                        {groupDialogMode === 'rename' ? 'Rename' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default UserManagement;
